import { z } from 'zod';
import { CommandType } from './commands.js';
import { Capabilities, Id, PlatformInfo } from './common.js';
import { LocationSource } from './devices.js';

/** Routes under /devices/me, called by agents with `Authorization: Bearer <token>`. */

export const MAX_FIXES_PER_BATCH = 500;

/** How far ahead of the server clock a fix may be timestamped before it's skipped. */
export const MAX_FUTURE_SKEW_MS = 5 * 60 * 1000;

export const LocationFix = z.object({
  /** Generated by the agent. Re-sending a fix with the same id is a no-op. */
  fixId: z.uuid(),
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  /** Radius in metres. IP-based fixes can be tens of kilometres. */
  accuracyM: z.number().min(0).max(10_000_000),
  altitude: z.number().min(-1_000).max(100_000).nullish(),
  /** Metres per second. */
  speed: z.number().min(0).max(1_000).nullish(),
  /** Degrees clockwise from north. */
  heading: z.number().min(0).max(360).nullish(),
  source: LocationSource,
  batteryPct: z.number().int().min(0).max(100).nullish(),
  isCharging: z.boolean().nullish(),
  recordedAt: z.iso.datetime({ offset: true }),
});
export type LocationFix = z.input<typeof LocationFix>;

/** POST /devices/me/location: one fix, or an offline catch-up batch. */
export const LocationIngestRequest = z.object({
  fixes: z.array(LocationFix).min(1).max(MAX_FIXES_PER_BATCH),
});
export type LocationIngestRequest = z.input<typeof LocationIngestRequest>;

/** What the agent needs to know about its own state, returned on every exchange. */
export const DeviceSelfState = z.object({
  deviceId: Id,
  name: z.string(),
  isLost: z.boolean(),
  lostMessage: z.string().nullable(),
  lostContact: z.string().nullable(),
});
export type DeviceSelfState = z.infer<typeof DeviceSelfState>;

export const LocationIngestResponse = z.object({
  accepted: z.number().int(),
  /** Already stored (a retried upload). */
  duplicates: z.number().int(),
  /** Timestamped too far in the future, or older than the retention period. */
  skipped: z.number().int(),
  device: DeviceSelfState,
});
export type LocationIngestResponse = z.infer<typeof LocationIngestResponse>;

/** POST /devices/me/status: anything that changed since the last report. */
export const DeviceStatusRequest = z
  .object({
    batteryPct: z.number().int().min(0).max(100).nullable(),
    isCharging: z.boolean().nullable(),
    sharingPaused: z.boolean(),
    capabilities: Capabilities,
    agentVersion: z.string().max(40),
    platformInfo: PlatformInfo,
  })
  .partial();
export type DeviceStatusRequest = z.input<typeof DeviceStatusRequest>;

export const DeviceStatusResponse = z.object({ device: DeviceSelfState });
export type DeviceStatusResponse = z.infer<typeof DeviceStatusResponse>;

/** A command as the agent sees it: just what to do. */
export const AgentCommand = z.object({
  id: Id,
  type: CommandType,
  payload: z.object({ message: z.string(), contact: z.string() }).nullable(),
});
export type AgentCommand = z.infer<typeof AgentCommand>;

/** GET /devices/me/commands: the agent's fallback when its push channel is down. */
export const DeviceCommandsResponse = z.object({
  commands: z.array(AgentCommand),
  device: DeviceSelfState,
});
export type DeviceCommandsResponse = z.infer<typeof DeviceCommandsResponse>;

/** Messages pushed to an agent over GET /devices/me/stream (a WebSocket). */
export const DeviceStreamMessage = z.discriminatedUnion('type', [
  z.object({ type: z.literal('command'), command: AgentCommand }),
  z.object({ type: z.literal('state'), device: DeviceSelfState }),
]);
export type DeviceStreamMessage = z.infer<typeof DeviceStreamMessage>;

/** Close code on the device stream when the device has been revoked or deleted. */
export const WS_CLOSE_DEVICE_REMOVED = 4003;
